/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { SweepstakesDetailsHeader } from '../SweepstakesDetailsPage/SweepstakeDetailsHeader'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  getSweepstakeDrawingInfoQueryKey,
  useGetSweepstakeByIdWithDrawingInfo,
} from 'hooks/api/useGetSweepstakeByIdWithDrawingInfo'
import { useGetSweepstakeById } from 'hooks/api/useGetSweepstakeById'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import _, { isNil } from 'lodash'
import { DataTable } from 'components/Shared/DataTable'
import noLocationFound from 'assets/locationNotFound.svg'
import { type GridRowParams } from '@mui/x-data-grid'
import {
  useEffect,
  useMemo,
  useState,
  type ElementType,
  type ReactNode,
} from 'react'
import { SweepstakesConfirmationModal } from './SweepstakesConfirmationModal'
import { useDrawSweepstakeWinners } from 'hooks/api/Sweepstake/useDrawSweepstakeWinners'
import {
  FilterStatusList,
  statusTextMap,
} from 'components/List/FilterStatusList'
import Close from '@mui/icons-material/Close'
import { SweepstakesWinnerActionsMenu } from 'components/Sweepstakes/SweepstakesWinnerActionsMenu'
import { NoResultsOverlayCustom } from 'components/DataTable/NoResultsOverlayCustom'
import { type TierType, type SweepstakeWinnerStatusType } from 'types/api'
import { useNotifyLocations } from 'hooks/api/Sweepstake/useNotifyLocations'
import { SweepstakesRequestRedrawModal } from './SweepstakesRequestRedrawModal'
import { useRedrawWinner } from 'hooks/api/Sweepstake/useRedrawWinner'
import { useSnackbar } from 'stores/useSnackbar'
import { getGrandPrizeWinnersColumns } from './GrandPrizeWinnersColumns'
import { getLocationWinnersColumns } from './LocationWinnersColumns'
import { type components } from 'api/playerPayback/api'
import { SweepstakesViewRedrawRequestModal } from './SweepstakesViewRedrawRequestModal'
import { useDenyLocationRedrawRequest } from 'hooks/api/Sweepstake/useDenyLocationRedrawRequest'
import { useRequestRedrawModal } from 'stores/useRequestRedrawModal'
import { AdminExportModal } from 'pages/AdminExport/AdminExportModal'
import ExportIcon from 'assets/export.svg'
import { LookupType } from 'utils/adminExportUtils'
import { useWinnersPagePersistence } from './useWinnersPagePersistence.ts'
import { SearchField } from 'components/SearchField.tsx'
import { useDebounce } from '@uidotdev/usehooks'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export interface SelectedWinnerType {
  locationName: string
  licenseNumber: string
  winnerName: string
  sweepstakesWinner: components['schemas']['FullSweepstakeWinnerDTO']
  drawingSequence: number
}

export interface GrandPrizeWinner {
  id: string
  winnerName: string
  patronId: number
  tierName: string
  prize: string
  contestWinnerId: number | null | undefined
  winnerStatus?: SweepstakeWinnerStatusType
  sweepstakeDrawingId: number
  contestEntryId: number
  drawnDate: string
  drawingSequence: number
  notes: string
  modifiedOn?: string
  isMultiWinner?: boolean
}

export const SweepstakesWinnersPage = () => {
  const CloseIconComponent = resolveIconComponent(Close)
  const { id: idParam } = useParams()
  const contestId = Number(idParam)
  const navigate = useNavigate()
  const location = useLocation()
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const sweepstakeBaseQuery = useGetSweepstakeById(contestId)
  const sweepstakeQuery = useGetSweepstakeByIdWithDrawingInfo(contestId)
  const [grandPrizeSearchKey, setGrandPrizeSearchKey] = useState('')
  const debouncedGrandPrizeSearchKey = useDebounce(grandPrizeSearchKey, 500)
  const [searchKey, setSearchKey] = useState('')
  const debouncedSearchKey = useDebounce(searchKey, 500)

  const {
    locationWinnersPaginationModel,
    setAndPersistLocationWinnersPaginationModel,
    saveCurrentScrollPosition,
  } = useWinnersPagePersistence({
    pathname: location.pathname,
  })

  // Mutations
  const drawSweepstakesWinnersMutation = useDrawSweepstakeWinners()
  const notifyLocationsMutation = useNotifyLocations()
  const redrawWinnerMutation = useRedrawWinner({
    invalidateQueryKeys: [getSweepstakeDrawingInfoQueryKey(contestId)],
    onSuccess: () => {
      setSnackbarMessage('Winner redrawn successfully', 'success')
    },
    onError: () => {
      setSnackbarMessage('Error redrawing winner', 'error')
    },
  })
  const denyLocationRedrawRequest = useDenyLocationRedrawRequest({
    invalidateQueryKeys: [getSweepstakeDrawingInfoQueryKey(contestId)],
    onSuccess: () => {
      setSnackbarMessage('Redraw request denied', 'success')
    },
    onError: () => {
      setSnackbarMessage('Error denying redraw request', 'error')
    },
  })

  // Redraw Winners Modal(this is the temp modal to redraw via button, will be removed)
  const [isRedrawWinnersModalOpen, setIsRedrawWinnersModalOpen] =
    useState(false)
  const toggleRedrawWinnersModalIsOpen = () =>
    setIsRedrawWinnersModalOpen(!isRedrawWinnersModalOpen)
  // Notify Winners Modal
  const [isNotifyWinnersModalOpen, setIsNotifyWinnersModalOpen] =
    useState(false)
  const toggleNotifyWinnersModalIsOpen = () =>
    setIsNotifyWinnersModalOpen(!isNotifyWinnersModalOpen)
  // Request New Winners Modal
  const { setData: setIsIndividualRedrawModalOpen } = useRequestRedrawModal()

  // View Request New Winners Modal
  const [isViewRequestRedrawModalOpen, setIsViewRequestRedrawModalOpen] =
    useState(false)
  const toggleViewRequestRedrawModalIsOpen = () =>
    setIsViewRequestRedrawModalOpen(!isViewRequestRedrawModalOpen)

  const [winnerStatusFilter, setWinnerStatusFilter] =
    useState<SweepstakeWinnerStatusType | null>(null)
  const [grandPrizeWinnerStatusFilter, setGrandPrizeWinnerStatusFilter] =
    useState<SweepstakeWinnerStatusType | null>(null)
  const [selectedWinnerRows, setSelectedWinnerRows] = useState<
    SelectedWinnerType[]
  >([])
  const removeWinnerStatusFilter = () => {
    setWinnerStatusFilter(null)
    setSelectedWinnerRows([])
  }
  const removeGrandPrizeWinnerStatusFilter = () => {
    setGrandPrizeWinnerStatusFilter(null)
  }

  const [isAdminExportModalOpen, setIsAdminExportModalOpen] = useState(false)
  const toggleAdminExportModal = () =>
    setIsAdminExportModalOpen(!isAdminExportModalOpen)

  const [lookupType, setLookupType] = useState<LookupType | undefined>(
    undefined
  )

  const handleExportClick = () => {
    setLookupType(LookupType.WinnersByContestId)
    toggleAdminExportModal()
  }

  const handleSelectAllWinnerRows = () => {
    const searchableRows = filteredLocationWinners.filter(
      (row) =>
        winnerStatusFilter === null || row.winnerStatus === winnerStatusFilter
    )
    const searchableRowIds = new Set(searchableRows.map((row) => row.id))
    const selectedSearchableRowsCount = selectedWinnerRows.filter((winner) =>
      searchableRowIds.has(winner.sweepstakesWinner.id)
    ).length

    if (
      searchableRows.length > 0 &&
      selectedSearchableRowsCount === searchableRows.length
    ) {
      setSelectedWinnerRows((prev) =>
        prev.filter(
          (winner) => !searchableRowIds.has(winner.sweepstakesWinner.id)
        )
      )
      return
    }

    const selectedIds = new Set(
      selectedWinnerRows.map((winner) => winner.sweepstakesWinner.id)
    )
    const rowsToAdd = searchableRows
      .filter((row) => !selectedIds.has(row.id))
      .map((row) => {
        return {
          locationName: row.leName,
          licenseNumber: row.leLicenseNumber,
          winnerName: row.winnerName,
          sweepstakesWinner: row.dto,
          drawingSequence: row.drawingSequence ?? 0,
        }
      })

    if (rowsToAdd.length > 0) {
      setSelectedWinnerRows((prev) => [...prev, ...rowsToAdd])
    }
  }

  const handleCheckboxChange = (selectedWinner: SelectedWinnerType) => {
    setSelectedWinnerRows((prev) => {
      const hasWinner = !isNil(
        selectedWinnerRows.find(
          (winner) =>
            winner.sweepstakesWinner.id === selectedWinner.sweepstakesWinner.id
        )
      )
      if (hasWinner) {
        return prev.filter(
          (winner) =>
            winner.sweepstakesWinner.id !== selectedWinner.sweepstakesWinner.id
        )
      } else {
        return [...prev, selectedWinner]
      }
    })
  }

  const handleRowClick = (params: GridRowParams) => {
    const clickedRow = params.row
    if (clickedRow.dto) {
      setSelectedWinner({
        locationName: clickedRow.leName,
        licenseNumber: clickedRow.leLicenseNumber,
        winnerName: clickedRow.winnerName,
        sweepstakesWinner: clickedRow.dto,
        drawingSequence: clickedRow.drawingSequence,
      })
    }
    if (clickedRow.winnerName !== '-') {
      saveCurrentScrollPosition()
      navigate(`/Winners/${params.id}`)
    }
  }

  const drawWinners = () => {
    if (isNil(sweepstake)) {
      return
    }
    drawSweepstakesWinnersMutation.mutate({ id: sweepstake.id })
  }

  const notifyLocations = () => {
    if (isNil(sweepstake)) {
      return
    }
    notifyLocationsMutation.mutate({ contestId: sweepstake.id })
  }

  const redrawWinners = (
    theReason: string,
    sweepstakeDrawingId: number,
    sweepstakeWinnerId?: number
  ) => {
    redrawWinnerMutation.mutate({
      sweepstakeDrawingId,
      redrawReason: theReason,
      sweepstakeWinnerId,
    })
  }

  const denyRedrawRequest = () => {
    if (selectedWinner?.sweepstakesWinner) {
      denyLocationRedrawRequest.mutate({
        sweepstakeDrawingId:
          selectedWinner.sweepstakesWinner.sweepstakeDrawingId,
      })
    }
  }

  const [selectedWinner, setSelectedWinner] = useState<SelectedWinnerType>()

  const contestWinnerById = useMemo(() => {
    return new Map(
      (sweepstakeQuery.data?.contestWinners ?? []).map((contestWinner) => [
        contestWinner.id,
        contestWinner,
      ])
    )
  }, [sweepstakeQuery.data?.contestWinners])

  const { locationDrawings, grandPrizeWinners, sweepstakeName } =
    useMemo(() => {
      const [allGrandPrizeDrawings, allLocationDrawings] = _.partition(
        sweepstakeQuery.data?.sweepstakeDrawings ?? [],
        (d) => d.type === 'GrandPrize'
      )

      //  Get the last 4 grand prize drawings
      //  There is one Sweepstake Drawing per Tier, and this ensures we only get
      //  the most recent drawing for each tier
      const grandPrizeDrawings = allGrandPrizeDrawings.slice(-4)

      // Use a Map to keep track of the most recent drawing for each licensedEstablishmentId
      const locationDrawingsByEstablishment = new Map<
        number,
        (typeof allLocationDrawings)[0]
      >()

      allLocationDrawings.forEach((ld) => {
        const establishmentId = ld.licensedEstablishment?.id
        if (establishmentId != null) {
          // Always set the latest occurrence of the location drawing
          locationDrawingsByEstablishment.set(establishmentId, ld)
        }
      })

      // Extract the values from the map, which are the most recent drawings for each establishment
      const locationDrawings = Array.from(
        locationDrawingsByEstablishment.values()
      )

      const tierOptions: TierType[] = ['Member', 'Insider', 'Elite', 'VIP']
      const grandPrizeWinners: GrandPrizeWinner[] = []
      grandPrizeDrawings.forEach((drawing) => {
        drawing.sweepstakeWinners
          ?.filter((winner) => winner.resultType === 'Winner')
          .forEach((winner) => {
            grandPrizeWinners.push({
              id: String(winner.id),
              winnerName: `${String(winner.patron?.firstName)} ${String(winner.patron?.lastName)}`,
              patronId: winner.patronId,
              tierName: tierOptions[Number(drawing.tierId) - 1],
              prize: isNil(winner.contestWinnerId)
                ? ''
                : (contestWinnerById.get(winner.contestWinnerId)
                    ?.prizeDescription ?? ''),
              contestWinnerId: winner.contestWinnerId,
              winnerStatus: winner.status,
              sweepstakeDrawingId: winner.sweepstakeDrawingId,
              contestEntryId: winner.contestEntryId,
              drawnDate: winner.dateDrawn ?? '-',
              drawingSequence: winner.drawingSequence ?? 0,
              notes: winner.notes ?? '',
              modifiedOn: winner.modifiedOn ?? '',
            })
          })
      })

      return {
        locationDrawings,
        grandPrizeWinners,
        sweepstakeName: sweepstakeQuery.data?.name,
      }
    }, [contestWinnerById, sweepstakeQuery.data])

  const locationWinners = useMemo(() => {
    return locationDrawings
      .flatMap((ld) => {
        const winners =
          ld.sweepstakeWinners?.map((winner) => {
            const fullName = `${String(winner.patron?.firstName)} ${String(winner.patron?.lastName)}`
            const validWinnerName = fullName.trim() !== ''
            return {
              id: winner.id,
              leName: ld.licensedEstablishment?.standardName ?? '???',
              leLicenseNumber: validWinnerName
                ? `#${String(ld.licensedEstablishment?.licenseNumber)}`
                : '-',
              winnerName: validWinnerName ? fullName : '-',
              patronId: winner.patronId,
              winnerStatus: winner.status ?? 'NoWinner',
              dto: winner,
              resultType: winner.resultType,
              drawingSequence: winner.drawingSequence,
              drawnDate: winner.dateDrawn ?? '-',
              notes: winner.notes ?? '',
              prize: isNil(winner.contestWinnerId)
                ? ''
                : (contestWinnerById.get(winner.contestWinnerId)
                    ?.prizeDescription ?? ''),
              contestWinnerId: winner.contestWinnerId,
            }
          }) ?? []

        // if a location has no winner, push a generic 'No Winner' object
        if (winners.length === 0) {
          winners.push({
            id: Number(ld?.licensedEstablishmentId),
            leName: ld.licensedEstablishment?.standardName ?? '???',
            leLicenseNumber: ld.licensedEstablishment?.licenseNumber
              ? `#${String(ld.licensedEstablishment.licenseNumber)}`
              : '???',
            winnerName: '-',
            patronId: -1,
            winnerStatus: 'NoWinner',
            dto: {
              id: -1,
              sweepstakeDrawingId: -1,
              contestEntryId: -1,
              patronId: -1,
              patronTierId: -1,
            },
            resultType: undefined,
            drawingSequence: -1, // put at end of the list,
            drawnDate: '-',
            notes: '',
            prize: '',
            contestWinnerId: null,
          })
        }

        const allIneligible = winners.every(
          (winner) => winner.resultType === 'Ineligible'
        )
        if (allIneligible && winners.length > 0) {
          return [
            {
              ...winners[0],
              winnerName: '-',
              winnerStatus: 'NoWinner',
            },
          ]
        }
        return winners
      })
      .filter(
        (winner) =>
          winner.resultType === 'Winner' || winner.winnerStatus === 'NoWinner'
      )
      .sort((a, b) => {
        return a.leName.localeCompare(b.leName)
      })
  }, [contestWinnerById, locationDrawings])

  const multiWinnerPatronIds = useMemo(() => {
    const winnerCountsByPatronId = new Map<number, number>()
    const winners = [...grandPrizeWinners, ...locationWinners]

    winners.forEach((winner) => {
      const isValidWinner =
        winner.patronId > 0 &&
        winner.winnerName !== '-' &&
        winner.winnerStatus !== 'NoWinner'

      if (!isValidWinner) {
        return
      }

      winnerCountsByPatronId.set(
        winner.patronId,
        (winnerCountsByPatronId.get(winner.patronId) ?? 0) + 1
      )
    })

    return new Set(
      Array.from(winnerCountsByPatronId.entries())
        .filter(([, count]) => count > 1)
        .map(([patronId]) => patronId)
    )
  }, [grandPrizeWinners, locationWinners])

  const locationWinnersWithMultiWinner = useMemo(() => {
    return locationWinners.map((winner) => ({
      ...winner,
      isMultiWinner:
        winner.patronId > 0 &&
        winner.winnerName !== '-' &&
        winner.winnerStatus !== 'NoWinner' &&
        multiWinnerPatronIds.has(winner.patronId),
    }))
  }, [locationWinners, multiWinnerPatronIds])

  const filteredLocationWinners = useMemo(() => {
    const normalizedSearchKey = debouncedSearchKey.trim().toLowerCase()

    if (normalizedSearchKey === '') {
      return locationWinnersWithMultiWinner
    }

    return locationWinnersWithMultiWinner.filter((winner) => {
      return (
        winner.leName.toLowerCase().includes(normalizedSearchKey) ||
        winner.winnerName.toLowerCase().includes(normalizedSearchKey) ||
        winner.dto.patron?.firstName
          ?.toLowerCase()
          .includes(normalizedSearchKey) ||
        winner.dto.patron?.lastName
          ?.toLowerCase()
          .includes(normalizedSearchKey) ||
        (
          String(winner.dto.patron?.firstName?.toLowerCase()) +
          ' ' +
          String(winner.dto.patron?.lastName?.toLowerCase())
        ).includes(normalizedSearchKey)
      )
    })
  }, [locationWinnersWithMultiWinner, debouncedSearchKey])

  useEffect(() => {
    if (locationWinners.length > 0) {
      setSelectedWinner({
        locationName: locationWinners[0].leName,
        licenseNumber: locationWinners[0].leLicenseNumber,
        winnerName: locationWinners[0].winnerName,
        sweepstakesWinner: locationWinners[0].dto,
        drawingSequence: locationWinners[0].drawingSequence ?? 0,
      })
    }
  }, [locationWinners])

  const locationWinnersColumns = getLocationWinnersColumns({
    contestId,
    locationWinners: filteredLocationWinners,
    handleCheckboxChange,
    handleSelectAllWinnerRows,
    selectedWinnerRows,
    winnerStatusFilter,
    setIsIndividualRedrawModalOpen,
    sweepstakeName: sweepstakeName ?? '',
  })

  // Show the bulk winner actions menu if a specific winner status filter is applied,
  // except for 'WinnerNotified' since those winners have already been notified and no further bulk actions are applicable
  const shouldShowBulkWinnerActionsMenu =
    winnerStatusFilter !== null && winnerStatusFilter !== 'WinnerNotified'

  let winnerActionsControl: ReactNode = null
  if (winnerStatusFilter === null) {
    winnerActionsControl = (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FilterStatusList
          buttonName="Filter"
          listName="Winner Statuses"
          statusFilter={winnerStatusFilter}
          setStatusFilter={setWinnerStatusFilter}
        />
      </Box>
    )
  } else if (shouldShowBulkWinnerActionsMenu) {
    winnerActionsControl = (
      <Box>
        <SweepstakesWinnerActionsMenu
          selectedWinners={selectedWinnerRows}
          clearSelectedWinners={() => setSelectedWinnerRows([])}
          winnerStatusFilter={winnerStatusFilter}
          sweepstakeName={sweepstakeName ?? ''}
          contestId={contestId}
        />
      </Box>
    )
  }

  const sweepstake = sweepstakeQuery.data ?? sweepstakeBaseQuery.data

  const isGrandPrizeTableLoading =
    sweepstakeQuery.isFetching && grandPrizeWinners.length === 0
  const isLocationWinnersTableLoading =
    sweepstakeQuery.isFetching && filteredLocationWinners.length === 0
  const tierIdByTierName: Record<string, number> = {
    Member: 1,
    Insider: 2,
    Elite: 3,
    VIP: 4,
  }

  const addNoWinnerRows = (
    gpWinners: GrandPrizeWinner[]
  ): GrandPrizeWinner[] => {
    const result: GrandPrizeWinner[] = [...gpWinners]
    const patronTiers = ['Member', 'Insider', 'Elite', 'VIP']
    const numGrandPrizeWinnersPerTier =
      sweepstake?.sweepstakeData?.numGrandPrizeWinnersPerTier ?? 1
    patronTiers.forEach((tier) => {
      const gpWinnersByTier = gpWinners.filter(
        (winner) => winner.tierName === tier
      )
      for (
        let i = 0;
        i < numGrandPrizeWinnersPerTier - gpWinnersByTier.length;
        i++
      ) {
        result.push({
          id: `no-winner-${tier}-${i}`,
          winnerName: '-',
          patronId: -1,
          tierName: tier,
          winnerStatus: 'NoWinner',
          sweepstakeDrawingId: -1,
          contestEntryId: -1,
          drawnDate: '-',
          prize: '',
          contestWinnerId: null,
          drawingSequence: i + 1,
          notes: '',
        })
      }
    })
    return result
  }

  const getGrandPrizeTierSortValue = (winner: GrandPrizeWinner): number => {
    winner.isMultiWinner =
      winner.patronId > 0 &&
      winner.winnerName !== '-' &&
      winner.winnerStatus !== 'NoWinner' &&
      multiWinnerPatronIds.has(winner.patronId)
    if (isNil(winner.contestWinnerId)) {
      const tierId = tierIdByTierName[winner.tierName]
      return isNil(tierId) ? Number.MAX_SAFE_INTEGER : -tierId
    }

    const tierId =
      contestWinnerById.get(winner.contestWinnerId)?.tierId ??
      tierIdByTierName[winner.tierName]

    return isNil(tierId) ? Number.MAX_SAFE_INTEGER : -tierId
  }

  const getGrandPrizeRowSortValue = (winner: GrandPrizeWinner): number => {
    if (isNil(winner.contestWinnerId)) {
      return winner.drawingSequence ?? Number.MAX_SAFE_INTEGER
    }

    return (
      contestWinnerById.get(winner.contestWinnerId)?.winnerNumber ??
      Number.MAX_SAFE_INTEGER
    )
  }

  const getGrandPrizeRowPlacementSortValue = (
    winner: GrandPrizeWinner
  ): number => {
    return winner.winnerStatus === 'NoWinner' ? 1 : 0
  }

  const sortedGrandPrizeRows = addNoWinnerRows([...grandPrizeWinners]).sort(
    (a, b) => {
      // sort by tiers first with VIP at the top and Member at the bottom
      const tierSortDiff =
        getGrandPrizeTierSortValue(a) - getGrandPrizeTierSortValue(b)
      if (tierSortDiff !== 0) {
        return tierSortDiff
      }

      const placementSortDiff =
        getGrandPrizeRowPlacementSortValue(a) -
        getGrandPrizeRowPlacementSortValue(b)
      if (placementSortDiff !== 0) {
        return placementSortDiff
      }

      // then sort by winnerNumber of the prize...(if no prize, sort by drawing sequence)
      const rowSortDiff =
        getGrandPrizeRowSortValue(a) - getGrandPrizeRowSortValue(b)
      if (rowSortDiff !== 0) {
        return rowSortDiff
      }

      return (a.drawingSequence ?? 0) - (b.drawingSequence ?? 0)
    }
  )

  const normalizedGrandPrizeSearchKey = debouncedGrandPrizeSearchKey
    .trim()
    .toLowerCase()
  const filteredGrandPrizeRows =
    normalizedGrandPrizeSearchKey === ''
      ? sortedGrandPrizeRows
      : sortedGrandPrizeRows.filter((winner) => {
          return winner.winnerName
            .toLowerCase()
            .includes(normalizedGrandPrizeSearchKey)
        })

  const statusFilteredGrandPrizeRows = useMemo(() => {
    if (grandPrizeWinnerStatusFilter === null) {
      return filteredGrandPrizeRows
    }

    return filteredGrandPrizeRows.filter(
      (winner) => winner.winnerStatus === grandPrizeWinnerStatusFilter
    )
  }, [filteredGrandPrizeRows, grandPrizeWinnerStatusFilter])

  const statusFilteredLocationWinners = useMemo(() => {
    if (winnerStatusFilter === null) {
      return filteredLocationWinners
    }

    return filteredLocationWinners.filter(
      (winner) => winner.winnerStatus === winnerStatusFilter
    )
  }, [filteredLocationWinners, winnerStatusFilter])

  const grandPrizeWinnersColumns = getGrandPrizeWinnersColumns({
    setIsIndividualRedrawModalOpen,
    contestId,
    sweepstakeName: sweepstake?.name ?? '',
  })

  if (
    isNil(sweepstake) &&
    sweepstakeBaseQuery.isPending &&
    sweepstakeQuery.isPending
  ) {
    return <ActivityIndicator />
  }

  if (sweepstakeQuery.isError || isNil(sweepstake)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  return (
    <>
      <SweepstakesDetailsHeader
        sweepstake={sweepstake}
        currentTab={`/Contests/Sweepstakes/${String(sweepstake.id)}/Winners`}
        actionButton={
          <Box>
            {(sweepstake.sweepstakeData?.state === 'InternalValidation' ||
              sweepstake.sweepstakeData?.state === 'WinnerValidation' ||
              sweepstake.sweepstakeData?.state === 'Closed') && (
              <Button
                sx={{ marginLeft: '15px' }}
                variant="contained"
                onClick={toggleNotifyWinnersModalIsOpen}
                disabled={
                  sweepstake.sweepstakeData?.state === 'WinnerValidation' ||
                  sweepstake.sweepstakeData?.state === 'Closed'
                }
              >
                {sweepstake.sweepstakeData?.state === 'WinnerValidation' ||
                sweepstake.sweepstakeData?.state === 'Closed'
                  ? 'Locations Notified'
                  : 'Notify Locations of Winners'}
              </Button>
            )}
          </Box>
        }
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 3,
              marginTop: 6,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                marginBottom: 3,
              }}
            >
              Grand Prize Winners
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {grandPrizeWinnerStatusFilter && (
                <Box
                  sx={{
                    display: 'flex',
                    mr: '10px',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    color="primary"
                    onClick={removeGrandPrizeWinnerStatusFilter}
                  >
                    <Typography
                      sx={{ mr: '3px' }}
                      variant="label-CTA"
                      color="primary"
                    >
                      {statusTextMap[grandPrizeWinnerStatusFilter]}
                    </Typography>
                    {CloseIconComponent ? (
                      <CloseIconComponent sx={{ mb: '5px' }} />
                    ) : null}
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterStatusList
                  buttonName="Filter"
                  listName="Winner Statuses"
                  statusFilter={grandPrizeWinnerStatusFilter}
                  setStatusFilter={setGrandPrizeWinnerStatusFilter}
                  mode="grandPrize"
                />
              </Box>
            </Box>
          </Box>
          <SearchField
            placeholder="Search Winner"
            onChange={(e) => setGrandPrizeSearchKey(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />

          {isGrandPrizeTableLoading ? (
            <ActivityIndicator margin={8} />
          ) : (
            <DataTable
              columns={grandPrizeWinnersColumns}
              rows={statusFilteredGrandPrizeRows}
              columnVisibilityModel={{ drawingSequence: false }} // hide drawing sequence column
              emptyDataImageSource={noLocationFound}
              noDataMessage={
                debouncedGrandPrizeSearchKey
                  ? `No results found for "${debouncedGrandPrizeSearchKey}".`
                  : 'After the sweepstake ends and winners are drawn, grand prize winners will be posted here.'
              }
              onRowClick={handleRowClick}
            />
          )}

          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 3,
                marginTop: 6,
              }}
            >
              <Typography variant="h2">Location Winners</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {winnerStatusFilter && (
                  <Box
                    sx={{
                      display: 'flex',
                      mr: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Button color="primary" onClick={removeWinnerStatusFilter}>
                      <Typography
                        sx={{ mr: '3px' }}
                        variant="label-CTA"
                        color="primary"
                      >
                        {statusTextMap[winnerStatusFilter]}
                      </Typography>
                      {CloseIconComponent ? (
                        <CloseIconComponent sx={{ mb: '5px' }} />
                      ) : null}
                    </Button>
                    {selectedWinnerRows.length > 0 && (
                      <Typography
                        sx={{ ml: '10px', mr: '10px' }}
                        color="text.secondary"
                      >
                        {`${selectedWinnerRows.length} Selected`}
                      </Typography>
                    )}
                  </Box>
                )}
                {winnerActionsControl}
              </Box>
            </Box>
            {isLocationWinnersTableLoading ? (
              <ActivityIndicator />
            ) : (
              <>
                <SearchField
                  placeholder="Search Establishment or Winner"
                  onChange={(e) => setSearchKey(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <DataTable
                  onRowClick={handleRowClick}
                  columns={locationWinnersColumns}
                  rows={statusFilteredLocationWinners}
                  paginationModel={locationWinnersPaginationModel}
                  onPaginationModelChange={(model) => {
                    setAndPersistLocationWinnersPaginationModel(model)
                  }}
                  emptyDataImageSource={noLocationFound}
                  noDataMessage={
                    debouncedSearchKey
                      ? `No results found for "${debouncedSearchKey}".`
                      : 'After the sweepstake ends and winners are drawn, location winners will be posted here.'
                  }
                  getRowClassName={(params) =>
                    isNil(
                      selectedWinnerRows.find(
                        (winner) =>
                          winner.sweepstakesWinner.id === params.row.id
                      )
                    )
                      ? ''
                      : `selected-Winner`
                  }
                  slots={{ noResultsOverlay: NoResultsOverlayCustom }}
                />
              </>
            )}
          </>
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={{
            marginTop: 5,
          }}
          onClick={handleExportClick}
        >
          <img src={ExportIcon} width={15} height={15} alt="Export icon" />
          <Typography variant="label-CTA">Export</Typography>
        </Button>
        <SweepstakesRequestRedrawModal redrawWinners={redrawWinners} />
        {selectedWinner && (
          <SweepstakesViewRedrawRequestModal
            locationName={selectedWinner.locationName}
            licenseNumber={selectedWinner.licenseNumber}
            winnerName={selectedWinner.winnerName}
            requestReason={selectedWinner.sweepstakesWinner.redrawReason ?? ''}
            isModalOpen={isViewRequestRedrawModalOpen}
            toggleIsOpen={toggleViewRequestRedrawModalIsOpen}
            onConfirm={(reason) => {
              redrawWinners(
                reason,
                selectedWinner.sweepstakesWinner.sweepstakeDrawingId
              )
              toggleViewRequestRedrawModalIsOpen()
            }}
            onDeny={() => {
              denyRedrawRequest()
              toggleViewRequestRedrawModalIsOpen()
            }}
            onCancel={toggleViewRequestRedrawModalIsOpen}
          />
        )}
        <SweepstakesConfirmationModal
          variant="Redraw"
          sweepstakeName={sweepstake.name}
          isModalOpen={isRedrawWinnersModalOpen}
          toggleIsOpen={toggleRedrawWinnersModalIsOpen}
          onConfirm={() => {
            toggleRedrawWinnersModalIsOpen()
            drawWinners()
          }}
          onCancel={toggleRedrawWinnersModalIsOpen}
        />
        <SweepstakesConfirmationModal
          variant="Notify"
          sweepstakeName={sweepstake.name}
          isModalOpen={isNotifyWinnersModalOpen}
          toggleIsOpen={toggleNotifyWinnersModalIsOpen}
          onConfirm={() => {
            toggleNotifyWinnersModalIsOpen()
            notifyLocations()
          }}
          onCancel={toggleNotifyWinnersModalIsOpen}
        />
      </SweepstakesDetailsHeader>
      <AdminExportModal
        isModalOpen={isAdminExportModalOpen}
        toggleModal={toggleAdminExportModal}
        header={sweepstake.name ?? ''}
        lookupType={lookupType}
        lookupId={idParam}
      />
    </>
  )
}
